import { LoaderFunction } from 'react-router-dom';
import { store } from '../../../../state/configureStore';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_ACTIONS } from '../../../../gql/queries/actions/actions';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';
import { selectActionOrders } from '../../../../gql/selectors/actions';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../gql/queries/actions/externalCompanies';
import { FETCH_ACTIVITYS } from '../../../../gql/queries/actions/activitys';

// Types
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { FETCH_MATERIALS } from '../../../../gql/queries/actions/materials';

export const ActionsValidationLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
    void refreshOrderFlags(userData);
    try {
      const cursorActions = getCursor(tableIds.VALIDATION_ACTIONS)(
        store.getState()
      );
      const dispatch = store.dispatch;

      // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
      let effectiveConcessionId = cursorActions.concessionId;
      if (!effectiveConcessionId) {
        try {
          const {
            data: { concessions },
          } = await client.query({ query: GET_CONCESSIONS });
          if (concessions?.length === 1) {
            effectiveConcessionId = concessions[0].id;
            dispatch(
              setCursor(tableIds.VALIDATION_ACTIONS, {
                ...cursorActions,
                concessionId: effectiveConcessionId,
              })
            );
          }
        } catch {
          /* continue without concessionId */
        }
      }

      if (userData?.user) {
        const roleActive = userData?.user?.user?.rolActive;

        // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
        // anterior interrumpido.
        dispatch(loadingData(tableIds.VALIDATION_ACTIONS, false));

        const readActions = client.readQuery({
          query: FETCH_ACTIONS,
          variables: {
            input: {
              take: cursorActions.take,
              cursor: cursorActions.cursor,
              concessionId: effectiveConcessionId,
              capitolId: cursorActions.capitolId,
              subCapitolId: cursorActions.subCapitolId,
              sortField: cursorActions.sortField,
              orderBy: cursorActions.orderBy,
              toDate: cursorActions.toDate,
              fromDate: cursorActions.fromDate,
              indexType: cursorActions.indexType,
              indexExpedition: cursorActions.indexExpedition,
              classification: cursorActions.classification,
              predefinedOrderId: cursorActions.predefinedOrderId,
              states: cursorActions.states,
              userIds: cursorActions.userIds,
              operatorIds: cursorActions.operatorIds,
              teamIds: cursorActions.teamIds,
              externalCompanyId: cursorActions.externalCompanyId,
              track: cursorActions.track,
              activityId: cursorActions.activityId,
              // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
              onlyFlagged: cursorActions?.onlyFlagged,
              // Filtro «Origen» (2026-08-05): el control lo pinta la tabla compartida
              // y el cursor lo guardaba, pero esta tabla nunca lo reenviaba a la
              // query — se seleccionaba y no filtraba nada. La de Órdenes de trabajo
              // → Actuaciones sí lo hacía, de ahí la asimetría que se reportó.
              containerTypes: cursorActions.containerTypes,
              pkInitKm: cursorActions.pkInitKm,
              pkInitMeter: cursorActions.pkInitMeter,
              pkEndKm: cursorActions.pkEndKm,
              pkEndMeter: cursorActions.pkEndMeter,
              description: cursorActions.description,
              laneCutting: cursorActions.laneCutting,
              materialIds: cursorActions.materialIds,
              hasMaterials: cursorActions.hasMaterials,
            },
          },
        });

        const totalCount = readActions?.actions?.totalCount;
        const pageInfo = readActions?.actions?.pageInfo;

        const isCacheEmpty = JSON.stringify(readActions) === '{}';

        const actions =
          readActions &&
          !isCacheEmpty &&
          (await selectActionOrders(readActions?.actions?.actions));

        const [
          {
            data: { capitols },
          },
          {
            data: { concessions },
          },
          {
            data: { subCapitols },
          },
          {
            data: { classifications },
          },
          {
            data: { teams },
          },
          {
            data: { externalCompanies },
          },
          {
            data: { activitys },
          },
          {
            data: { predefinedOrders },
          },
          {
            data: { users },
          },
        ] = await Promise.all([
          client.query({
            query: FETCH_CAPITOLS,
          }),
          client.query({
            query: GET_CONCESSIONS,
          }),
          client.query({
            query: FETCH_SUB_CAPITOLS,
          }),
          client.query({
            query: FETCH_CLASSIFICATIONS,
          }),
          client.query({
            query: FETCH_TEAMS,
          }),
          client.query({
            query: FETCH_EXTERNAL_COMPANIES,
          }),
          client.query({
            query: FETCH_ACTIVITYS,
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: FETCH_PREDEFINED_ORDERS,
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: FETCH_USERS,
            fetchPolicy: 'cache-first',
          }),
        ]);

        const filtersDataOptions = {
          // Opciones del filtro de materiales. Sale del catálogo ya cacheado

          // (stale-while-revalidate, `cache-only` en la 1ª lectura), así que

          // sin cobertura no tumba el loader.

          materials: await client
            .query({ query: FETCH_MATERIALS })
            .then((r: any) => r.data?.materials ?? [])
            .catch(() => []),
          capitols,
          concessions,
          subCapitols,
          classifications,
          teams,
          externalCompanies,
          activitys,
          predefinedOrders,
          users,
        };

        const combinedData = {
          actions,
          filtersDataOptions,
          totalCount,
          pageInfo,
          roleActive,
        };

        if (!readActions || isCacheEmpty) {
          dispatch(loadingData(tableIds.VALIDATION_ACTIONS, true));
          try {
            let data;
            if (isCacheEmpty) {
              // Tarea #11: respetar el take guardado en cursor (page-size selector).
              const effectiveTake = firstPageTake(cursorActions.take, takeBase);
              data = await client.query({
                query: FETCH_ACTIONS,
                variables: {
                  input: {
                    take: effectiveTake,
                    cursor: '',
                    concessionId: effectiveConcessionId,
                    capitolId: cursorActions.capitolId,
                    subCapitolId: cursorActions.subCapitolId,
                    sortField: cursorActions.sortField,
                    orderBy: cursorActions.orderBy ?? 'desc',
                    toDate: cursorActions.toDate,
                    fromDate: cursorActions.fromDate,
                    indexType: cursorActions.indexType,
                    indexExpedition: cursorActions.indexExpedition,
                    classification: cursorActions.classification,
                    predefinedOrderId: cursorActions.predefinedOrderId,
                    states: cursorActions.states,
                    userIds: cursorActions.userIds,
                    operatorIds: cursorActions.operatorIds,
                    teamIds: cursorActions.teamIds,
                    externalCompanyId: cursorActions.externalCompanyId,
                    track: cursorActions.track,
                    activityId: cursorActions.activityId,
                    onlyFlagged: cursorActions?.onlyFlagged,
                    containerTypes: cursorActions.containerTypes,
                    pkInitKm: cursorActions.pkInitKm,
                    pkInitMeter: cursorActions.pkInitMeter,
                    pkEndKm: cursorActions.pkEndKm,
                    pkEndMeter: cursorActions.pkEndMeter,
                    description: cursorActions.description,
                    laneCutting: cursorActions.laneCutting,
                    materialIds: cursorActions.materialIds,
                    hasMaterials: cursorActions.hasMaterials,
                  },
                },
              });

              const setCursorData = {
                take: effectiveTake,
                cursor: '',
                concessionId: effectiveConcessionId,
                capitolId: cursorActions.capitolId,
                subCapitolId: cursorActions.subCapitolId,
                toDate: cursorActions.toDate,
                fromDate: cursorActions.fromDate,
                indexType: cursorActions.indexType,
                indexExpedition: cursorActions.indexExpedition,
                classification: cursorActions.classification,
                predefinedOrderId: cursorActions.predefinedOrderId,
                states: cursorActions.states,
                userIds: cursorActions.userIds,
                operatorIds: cursorActions.operatorIds,
                teamIds: cursorActions.teamIds,
                externalCompanyId: cursorActions.externalCompanyId,
                track: cursorActions.track,
                activityId: cursorActions.activityId,
                onlyFlagged: cursorActions?.onlyFlagged,
                containerTypes: cursorActions.containerTypes,
                pkInitKm: cursorActions.pkInitKm,
                pkInitMeter: cursorActions.pkInitMeter,
                pkEndKm: cursorActions.pkEndKm,
                pkEndMeter: cursorActions.pkEndMeter,
                description: cursorActions.description,
                laneCutting: cursorActions.laneCutting,
                materialIds: cursorActions.materialIds,
                hasMaterials: cursorActions.hasMaterials,
              };

              dispatch(setCursor(tableIds.VALIDATION_ACTIONS, setCursorData));
            } else {
              data = await client.query({
                query: FETCH_ACTIONS,
                variables: {
                  input: {
                    take: cursorActions.take,
                    cursor: cursorActions.cursor,
                    concessionId: effectiveConcessionId,
                    capitolId: cursorActions.capitolId,
                    subCapitolId: cursorActions.subCapitolId,
                    sortField: cursorActions.sortField,
                    orderBy: cursorActions.orderBy,
                    toDate: cursorActions.toDate,
                    fromDate: cursorActions.fromDate,
                    indexType: cursorActions.indexType,
                    indexExpedition: cursorActions.indexExpedition,
                    classification: cursorActions.classification,
                    predefinedOrderId: cursorActions.predefinedOrderId,
                    states: cursorActions.states,
                    userIds: cursorActions.userIds,
                    operatorIds: cursorActions.operatorIds,
                    teamIds: cursorActions.teamIds,
                    externalCompanyId: cursorActions.externalCompanyId,
                    track: cursorActions.track,
                    activityId: cursorActions.activityId,
                    onlyFlagged: cursorActions?.onlyFlagged,
                    containerTypes: cursorActions.containerTypes,
                    pkInitKm: cursorActions.pkInitKm,
                    pkInitMeter: cursorActions.pkInitMeter,
                    pkEndKm: cursorActions.pkEndKm,
                    pkEndMeter: cursorActions.pkEndMeter,
                    description: cursorActions.description,
                    laneCutting: cursorActions.laneCutting,
                    materialIds: cursorActions.materialIds,
                    hasMaterials: cursorActions.hasMaterials,
                  },
                },
              });
            }

            const loading = data.loading;
            const actions = await selectActionOrders(data.data.actions.actions);

            const totalCount = data.data.actions.totalCount;

            const pageInfo = data.data.actions.pageInfo;

            const [
              {
                data: { capitols },
              },
              {
                data: { concessions },
              },
              {
                data: { subCapitols },
              },
              {
                data: { classifications },
              },
              {
                data: { teams },
              },
              {
                data: { externalCompanies },
              },
              {
                data: { predefinedOrders },
              },
              {
                data: { users },
              },
            ] = await Promise.all([
              client.query({
                query: FETCH_CAPITOLS,
              }),
              client.query({
                query: GET_CONCESSIONS,
              }),
              client.query({
                query: FETCH_SUB_CAPITOLS,
              }),
              client.query({
                query: FETCH_CLASSIFICATIONS,
              }),
              client.query({
                query: FETCH_TEAMS,
              }),
              client.query({
                query: FETCH_EXTERNAL_COMPANIES,
              }),
              client.query({
                query: FETCH_PREDEFINED_ORDERS,
                fetchPolicy: 'cache-first',
              }),
              client.query({
                query: FETCH_USERS,
                fetchPolicy: 'cache-first',
              }),
            ]);

            const filtersDataOptions = {
              // Opciones del filtro de materiales. Sale del catálogo ya cacheado

              // (stale-while-revalidate, `cache-only` en la 1ª lectura), así que

              // sin cobertura no tumba el loader.

              materials: await client
                .query({ query: FETCH_MATERIALS })
                .then((r: any) => r.data?.materials ?? [])
                .catch(() => []),
              capitols,
              concessions,
              subCapitols,
              classifications,
              teams,
              externalCompanies,
              predefinedOrders,
              users,
            };

            const combinedData = {
              actions,
              filtersDataOptions,
              totalCount,
              pageInfo,
              loading,
              roleActive,
            };

            return combinedData;
          } finally {
            // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
            dispatch(loadingData(tableIds.VALIDATION_ACTIONS, false));
          }
        }
        return combinedData;
      }

      return undefined;
    } catch (err) {
      console.log('Loader offline fallback:', err);
      // FIX (loader-loading-flag-leak): defensa para errores antes del MISS branch.
      store.dispatch(loadingData(tableIds.VALIDATION_ACTIONS, false));
      return {
        actions: [],
        filtersDataOptions: {
          capitols: [],
          concessions: [],
          subCapitols: [],
          classifications: [],
          teams: [],
          externalCompanies: [],
          activitys: [],
          predefinedOrders: [],
          users: [],
        },
        totalCount: 0,
        pageInfo: { hasNextPage: false, hasPreviousPage: false },
        roleActive: userData?.user?.user?.rolActive,
      };
    }
  };

  return { data: load() };
};
